import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
	Badge,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	Grid,
	HStack,
	Stack,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	VStack,
	useColorMode,
	useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment/moment";

import Loader from "../../Components/Loader/Loader";
import TablePagination from "../../Components/Pagination/TablePagination";
import TicketsTableRow from "../../Components/TableRows/TicketsTableRow";
import { selectActiveProperties } from "../../redux/user/userSlice";
import { buildAdministrationFeeChartConfig } from "../../utils/dashboardAdministrationFee";
import { documentTypes, fullDateFormat } from "../../utils/types";
import { getPropertyAddress } from "../../utils/verifier";

export default function Dashboard() {
	const textColor = useColorModeValue("gray.700", "white");
	const properties = useSelector(selectActiveProperties);
	const { t } = useTranslation();
	const name = useSelector((state) => state.user.user?.name);
	const email = useSelector((state) => state.user.user?.email);
	const phoneNumber = useSelector((state) => state.user.user?.phoneNumber);
	const { colorMode } = useColorMode();
	const [loading, setLoading] = useState(true);
	const [chartsLoading, setChartsLoading] = useState(true);
	const [tickets, setTickets] = useState();
	const [selectedYear, setSelectedYear] = useState(moment().year());
	const [monthlyAdministrationFeeChart, setMonthlyAdministrationFeeChart] = useState();
	const borderColor = useColorModeValue("gray.200", "gray.700");
	const headerBg = useColorModeValue("white", "gray.800");
	const currentYear = moment().year();

	const ITEMS_PER_PAGE = 5;
	const [currentPage, setCurrentPage] = useState(1);
	const totalTickets = tickets?.length || 0;
	const totalPages = Math.ceil(totalTickets / ITEMS_PER_PAGE);
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIndex = startIndex + ITEMS_PER_PAGE;
	const paginatedTickets = tickets?.slice(startIndex, endIndex);

	const expiringLeases = properties
		.map((property) => {
			const lease = property.documents.find((doc) => doc.type === "lease" && doc.active);
			const extension = property.documents.find((doc) => doc.type === "extension" && doc.active);
			if (!lease) return {};
			if (!extension) {
				if (moment(lease.expirationDate).unix() - moment().unix() < 2592000)
					return {
						owner: property.owner.name,
						address: getPropertyAddress(property),
						expirationDate: lease.expirationDate,
						type: "lease",
						propertyId: property.id,
					};
				return {};
			}
			if (moment(extension.expirationDate).unix() - moment().unix() < 2592000)
				return {
					owner: property.owner.name,
					address: getPropertyAddress(property),
					expirationDate: extension.expirationDate,
					type: "extension",
					propertyId: property.id,
				};
			return {};
		})
		.filter((entry) => entry.address)
		.sort((a, b) => moment(a.expirationDate).unix() - moment(b.expirationDate).unix());

	const expiringDocuments = properties
		.map((property) => {
			const documents = property.documents.filter(
				(doc) => doc.active && doc.expirationDate && documentTypes.map((entry) => entry.id).includes(doc.type),
			);
			const expDocs = documents.filter(
				(doc) => moment(doc.expirationDate).unix() - moment().unix() < (doc.type.toLowerCase().includes("insurance") ? 604800 : 2592000),
			);
			return expDocs.map((doc) => ({
				owner: property.owner.name,
				address: getPropertyAddress(property),
				expirationDate: doc.expirationDate,
				type: doc.type,
				propertyId: property.id,
			}));
		})
		.flat()
		.filter((entry) => entry.address)
		.sort((a, b) => moment(a.expirationDate).unix() - moment(b.expirationDate).unix());

	const LEASES_PER_PAGE = 5;
	const [leasesPage, setLeasesPage] = useState(1);
	const totalLeases = expiringLeases?.length || 0;
	const totalLeasesPages = Math.ceil(totalLeases / LEASES_PER_PAGE);
	const leasesStartIndex = (leasesPage - 1) * LEASES_PER_PAGE;
	const leasesEndIndex = leasesStartIndex + LEASES_PER_PAGE;
	const paginatedLeases = expiringLeases?.slice(leasesStartIndex, leasesEndIndex);

	const DOCS_PER_PAGE = 5;
	const [docsPage, setDocsPage] = useState(1);
	const totalDocs = expiringDocuments?.length || 0;
	const totalDocsPages = Math.ceil(totalDocs / DOCS_PER_PAGE);
	const docsStartIndex = (docsPage - 1) * DOCS_PER_PAGE;
	const docsEndIndex = docsStartIndex + DOCS_PER_PAGE;
	const paginatedDocs = expiringDocuments?.slice(docsStartIndex, docsEndIndex);

	useEffect(() => {
		if (!properties) return;

		setLoading(true);
		const openTickets = properties
			.map((property) =>
				property.tickets.map((ticket) => ({
					...ticket,
					address: getPropertyAddress(property),
					propertyId: property.id,
					owner: property.owner,
				})),
			)
			.flat()
			.filter((ticket) => ticket.status.toLowerCase() !== "done");
		setTickets(openTickets);
		setLoading(false);
	}, [properties]);

	useEffect(() => {
		if (!properties) return;

		setChartsLoading(true);
		const timer = setTimeout(() => {
			setMonthlyAdministrationFeeChart(buildAdministrationFeeChartConfig(properties, selectedYear));
			setChartsLoading(false);
		}, 0);

		return () => clearTimeout(timer);
	}, [properties, selectedYear]);

	const navigate = useNavigate();
	const goToProperty = (id, tab) => {
		navigate(`/properties/view/${id}?tab=${tab}`);
	};
	const getDaysUntilExpiredBadge = (date) => {
		const daysUntilExpired = moment(date).diff(moment(), "days");
		if (daysUntilExpired < 0)
			return (
				<VStack spacing={1} align="start">
					<Text>{moment(date).format(fullDateFormat)}</Text>
					<Badge colorScheme="red">expired</Badge>
				</VStack>
			);
		return (
			<VStack spacing={1} align="start">
				<Text>{moment(date).format(fullDateFormat)}</Text>
				<Badge colorScheme="yellow">{daysUntilExpired} zile</Badge>
			</VStack>
		);
	};
	const hoverColor = useColorModeValue("gray.100", "gray.700");

	const LeaseRow = React.memo(function LeaseRow({ el }) {
		return (
			<Tr onClick={() => goToProperty(el.propertyId, "tenants")} _hover={{ bg: hoverColor, cursor: "pointer" }}>
				<Td whiteSpace="nowrap" borderBottom="1px" borderColor={borderColor}>
					{getDaysUntilExpiredBadge(el.expirationDate)}
				</Td>
				<Td borderBottom="1px" borderColor={borderColor}>
					<Tooltip label={`${el.address} - ${el.owner}`} placement="top">
						<VStack align="stretch" spacing={0}>
							<Text noOfLines={1}>{el.address}</Text>
							<Text fontSize="xs" color="gray.500" noOfLines={1}>
								{el.owner}
							</Text>
						</VStack>
					</Tooltip>
				</Td>
			</Tr>
		);
	});

	const DocumentRow = React.memo(function DocumentRow({ el }) {
		return (
			<Tr onClick={() => goToProperty(el.propertyId, "documents")} _hover={{ bg: hoverColor, cursor: "pointer" }}>
				<Td whiteSpace="nowrap" borderBottom="1px" borderColor={borderColor}>
					{getDaysUntilExpiredBadge(el.expirationDate)}
				</Td>
				<Td borderBottom="1px" borderColor={borderColor}>
					<Text noOfLines={1}>{t(`types.${el.type}`)}</Text>
				</Td>
				<Td borderBottom="1px" borderColor={borderColor}>
					<Tooltip label={`${el.address} - ${el.owner}`} placement="top">
						<VStack align="stretch" spacing={0}>
							<Text noOfLines={1}>{el.address}</Text>
							<Text fontSize="xs" color="gray.500" noOfLines={1}>
								{el.owner}
							</Text>
						</VStack>
					</Tooltip>
				</Td>
			</Tr>
		);
	});

	return (
		<React.Fragment>
			<Flex flexDirection="column" marginBottom={4}>
				<Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="24px">
					<Card>
						<CardHeader fontSize="sm" fontWeight="bold" pb=".1rem">
							<HStack spacing={3} justify="space-between">
								<Text>{t("dashboard.expiringLeases")}</Text>
								{expiringLeases.length > 0 && (
									<Badge colorScheme="yellow" fontSize="sm">
										{expiringLeases.length}
									</Badge>
								)}
							</HStack>
						</CardHeader>
						<CardBody>
							<VStack spacing={4} align="stretch">
								{loading ? (
									<Loader text={t("misc.loading")} />
								) : (
									<>
										<TableContainer maxHeight="300px" overflowY="auto">
											<Table size="sm" variant="simple">
												<Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
													<Tr>
														<Th>{t("table.endDate")}</Th>
														<Th>{t("table.address")}</Th>
													</Tr>
												</Thead>
												<Tbody>
													{paginatedLeases?.map((el, index) => (
														<LeaseRow key={index} el={el} />
													))}
													{expiringLeases.length === 0 && (
														<Tr>
															<Td colSpan={2} textAlign="center" color="gray.500">
																{t("table.noData")}
															</Td>
														</Tr>
													)}
												</Tbody>
											</Table>
										</TableContainer>
										{expiringLeases.length > LEASES_PER_PAGE && (
											<TablePagination
												currentPage={leasesPage}
												totalPages={totalLeasesPages}
												totalItems={totalLeases}
												onPageChange={setLeasesPage}
											/>
										)}
									</>
								)}
							</VStack>
						</CardBody>
					</Card>
					<Card>
						<CardHeader fontSize="sm" fontWeight="bold" pb=".1rem">
							<HStack spacing={3} justify="space-between">
								<Text>{t("dashboard.expiringDocuments")}</Text>
								{expiringDocuments.length > 0 && (
									<Badge colorScheme="red" fontSize="sm">
										{expiringDocuments.length}
									</Badge>
								)}
							</HStack>
						</CardHeader>
						<CardBody>
							<VStack spacing={4} align="stretch">
								{loading ? (
									<Loader text={t("misc.loading")} />
								) : (
									<>
										<TableContainer maxHeight="300px" overflowY="auto">
											<Table size="sm" variant="simple">
												<Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
													<Tr>
														<Th>{t("table.endDate")}</Th>
														<Th>{t("table.type")}</Th>
														<Th>{t("table.address")}</Th>
													</Tr>
												</Thead>
												<Tbody>
													{paginatedDocs?.map((el, index) => (
														<DocumentRow key={index} el={el} />
													))}
													{expiringDocuments.length === 0 && (
														<Tr>
															<Td colSpan={3} textAlign="center" color="gray.500">
																{t("table.noData")}
															</Td>
														</Tr>
													)}
												</Tbody>
											</Table>
										</TableContainer>
										{expiringDocuments.length > DOCS_PER_PAGE && (
											<TablePagination
												currentPage={docsPage}
												totalPages={totalDocsPages}
												totalItems={totalDocs}
												onPageChange={setDocsPage}
											/>
										)}
									</>
								)}
							</VStack>
						</CardBody>
					</Card>
				</Grid>
			</Flex>

			<Flex flexDirection="column" marginBottom={4}>
				<Card p="1rem">
					<CardHeader>
						<Stack direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} spacing={3}>
							<Text fontSize="xl" color={textColor} fontWeight="bold">
								{t("dashboard.administrationFee")}
							</Text>
							<HStack spacing={3}>
								<Button size="sm" onClick={() => setSelectedYear((year) => year - 1)}>
									{t("table.previousYear")}
								</Button>
								<Text fontWeight="bold" minW="4rem" textAlign="center">
									{selectedYear}
								</Text>
								<Button size="sm" isDisabled={selectedYear >= currentYear} onClick={() => setSelectedYear((year) => year + 1)}>
									{t("table.nextYear")}
								</Button>
							</HStack>
						</Stack>
					</CardHeader>
					<CardBody justifyContent="center" alignItems="center" width="100%">
						{chartsLoading ? (
							<Loader text={t("misc.loading")} />
						) : monthlyAdministrationFeeChart ? (
							<Chart
								type="bar"
								options={{
									...monthlyAdministrationFeeChart.options,
									xaxis: {
										...monthlyAdministrationFeeChart.options.xaxis,
										labels: {
											...monthlyAdministrationFeeChart.options.xaxis.labels,
											style: { ...monthlyAdministrationFeeChart.options.xaxis.labels.style, colors: textColor },
										},
									},
									yaxis: {
										...monthlyAdministrationFeeChart.options.yaxis,
										labels: {
											...monthlyAdministrationFeeChart.options.yaxis.labels,
											style: { ...monthlyAdministrationFeeChart.options.yaxis.labels.style, colors: textColor },
										},
									},
									tooltip: { ...monthlyAdministrationFeeChart.options.tooltip, theme: colorMode },
								}}
								series={monthlyAdministrationFeeChart.series}
								style={{ width: "100%" }}
								height="300"
							/>
						) : null}
					</CardBody>
				</Card>
			</Flex>

			<Grid marginBottom={4}>
				<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
					<CardHeader>
						<Text fontSize="xl" fontWeight="bold">
							{t("dashboard.openTickets")}
						</Text>
					</CardHeader>
					<CardBody>
						<VStack spacing={4} align="stretch">
							<TableContainer>
								<Table size="sm">
									<Thead>
										<Tr>
											<Th>{t("table.addedDate")}</Th>
											<Th>{t("table.owner")}</Th>
											<Th>{t("table.address")}</Th>
											<Th>{t("table.status")}</Th>
											<Th>{t("table.title")}</Th>
											<Th>{t("table.author")}</Th>
											<Th></Th>
										</Tr>
									</Thead>
									<Tbody>
										{paginatedTickets?.map((ticket, index) => (
											<TicketsTableRow key={index} row={ticket} author={{ name, phoneNumber, email }} owner={ticket.owner} />
										))}
										{tickets?.length === 0 && (
											<Tr>
												<Td colSpan={7}>{t("table.noData")}</Td>
											</Tr>
										)}
									</Tbody>
								</Table>
							</TableContainer>
							{tickets?.length > 0 && (
								<TablePagination
									currentPage={currentPage}
									totalPages={totalPages}
									totalItems={totalTickets}
									onPageChange={setCurrentPage}
									itemsName={t("tickets.tickets")}
								/>
							)}
						</VStack>
					</CardBody>
				</Card>
			</Grid>
		</React.Fragment>
	);
}
